/**
 * Firestore CRUD for PDF measurements + per-page calibrations.
 *
 * Two collections:
 *   - pdfMeasurementCalibrations  (one doc per documentId+pageNumber)
 *   - pdfMeasurements             (many per documentId+pageNumber)
 *
 * Coordinate convention: normalized 0-100 across the page (matches MarkupLayer / AnnotationLayer).
 */

import { db } from './firebase';
import {
  collection,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  doc,
  Timestamp,
} from 'firebase/firestore';
import type { PdfMeasurement, PdfMeasurementCalibration } from '../types';
import { removeUndefinedDeep } from './removeUndefinedDeep';

const CAL_COLLECTION = 'pdfMeasurementCalibrations';
const MEAS_COLLECTION = 'pdfMeasurements';

/* ------------------------------------------------------------------ */
/* Mappers                                                             */
/* ------------------------------------------------------------------ */

function calibrationFromDoc(snap: { id: string; data: () => Record<string, unknown> }): PdfMeasurementCalibration {
  const d = snap.data();
  const created = d.createdAt as { toDate?: () => Date } | undefined;
  const updated = d.updatedAt as { toDate?: () => Date } | undefined;
  return {
    id: snap.id,
    documentId: d.documentId as string,
    documentUrl: d.documentUrl as string,
    pageNumber: d.pageNumber as number,
    mode: (d.mode as 'standard' | 'two-point') || 'standard',
    standardScaleId: (d.standardScaleId as string) || undefined,
    pointA: (d.pointA as { x: number; y: number }) || undefined,
    pointB: (d.pointB as { x: number; y: number }) || undefined,
    realDistance: typeof d.realDistance === 'number' ? d.realDistance : undefined,
    realDistanceUnit: (d.realDistanceUnit as PdfMeasurementCalibration['realDistanceUnit']) || undefined,
    realPerPdfPoint: typeof d.realPerPdfPoint === 'number' ? d.realPerPdfPoint : 0,
    unitSystem: (d.unitSystem as 'imperial' | 'metric') || 'imperial',
    baseUnit: (d.baseUnit as PdfMeasurementCalibration['baseUnit']) || 'ft',
    displayUnit: (d.displayUnit as PdfMeasurementCalibration['displayUnit']) || 'ft-in',
    pageNaturalWidth: typeof d.pageNaturalWidth === 'number' ? d.pageNaturalWidth : 612,
    pageNaturalHeight: typeof d.pageNaturalHeight === 'number' ? d.pageNaturalHeight : 792,
    leadId: (d.leadId as string) || undefined,
    projectId: (d.projectId as string) || undefined,
    userId: (d.userId as string) || undefined,
    userName: (d.userName as string) || undefined,
    createdAt: created?.toDate?.() ?? new Date(),
    updatedAt: updated?.toDate?.() ?? undefined,
  };
}

function measurementFromDoc(snap: { id: string; data: () => Record<string, unknown> }): PdfMeasurement {
  const d = snap.data();
  const created = d.createdAt as { toDate?: () => Date } | undefined;
  const updated = d.updatedAt as { toDate?: () => Date } | undefined;
  return {
    id: snap.id,
    documentId: d.documentId as string,
    documentUrl: d.documentUrl as string,
    pageNumber: d.pageNumber as number,
    kind: (d.kind as PdfMeasurement['kind']) || 'linear',
    points: (d.points as { x: number; y: number }[]) || [],
    label: (d.label as string) || undefined,
    leadId: (d.leadId as string) || undefined,
    projectId: (d.projectId as string) || undefined,
    userId: d.userId as string,
    userName: (d.userName as string) || 'Unknown',
    createdAt: created?.toDate?.() ?? new Date(),
    updatedAt: updated?.toDate?.() ?? undefined,
  };
}

/* ------------------------------------------------------------------ */
/* Calibration CRUD                                                    */
/* ------------------------------------------------------------------ */

/** Single-page calibration subscription. */
export function subscribePageCalibration(
  documentId: string,
  pageNumber: number,
  callback: (cal: PdfMeasurementCalibration | null) => void,
): () => void {
  const q = query(
    collection(db, CAL_COLLECTION),
    where('documentId', '==', documentId),
    where('pageNumber', '==', pageNumber),
  );
  return onSnapshot(q, snap => {
    if (snap.empty) {
      callback(null);
      return;
    }
    // If multiple, prefer most recent.
    const list = snap.docs.map(d => calibrationFromDoc({ id: d.id, data: () => d.data() }));
    list.sort((a, b) => (b.updatedAt?.getTime() || b.createdAt.getTime()) - (a.updatedAt?.getTime() || a.createdAt.getTime()));
    callback(list[0]);
  });
}

/** Subscribe to ALL page calibrations for a document — used by toolbar to surface "scale set on N pages". */
export function subscribeDocumentCalibrations(
  documentId: string,
  callback: (cals: PdfMeasurementCalibration[]) => void,
): () => void {
  const q = query(collection(db, CAL_COLLECTION), where('documentId', '==', documentId));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => calibrationFromDoc({ id: d.id, data: () => d.data() })));
  });
}

/** Upsert calibration for a (documentId, pageNumber). Replaces any existing. */
export async function saveCalibration(input: Omit<PdfMeasurementCalibration, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  const existing = await getDocs(
    query(
      collection(db, CAL_COLLECTION),
      where('documentId', '==', input.documentId),
      where('pageNumber', '==', input.pageNumber),
    ),
  );
  const payload = removeUndefinedDeep({
    ...input,
    updatedAt: Timestamp.now(),
  });
  if (existing.empty) {
    const ref = await addDoc(collection(db, CAL_COLLECTION), {
      ...payload,
      createdAt: Timestamp.now(),
    });
    return ref.id;
  }
  // Replace the first match; delete extras (defensive).
  const [first, ...rest] = existing.docs;
  await setDoc(doc(db, CAL_COLLECTION, first.id), {
    ...payload,
    createdAt: first.data().createdAt || Timestamp.now(),
  });
  for (const extra of rest) {
    try { await deleteDoc(doc(db, CAL_COLLECTION, extra.id)); } catch { /* ignore */ }
  }
  return first.id;
}

/**
 * Copy the resolved calibration to every page of the document. Pages that already
 * have a calibration are overwritten so the user gets a single, consistent scale.
 */
export async function applyCalibrationToAllPages(
  documentId: string,
  totalPages: number,
  base: Omit<PdfMeasurementCalibration, 'id' | 'createdAt' | 'updatedAt' | 'pageNumber'>,
): Promise<void> {
  await Promise.all(
    Array.from({ length: totalPages }, (_, i) => i + 1).map(pn =>
      saveCalibration({ ...base, pageNumber: pn }),
    ),
  );
}

export async function deletePageCalibration(calibrationId: string): Promise<void> {
  await deleteDoc(doc(db, CAL_COLLECTION, calibrationId));
}

/* ------------------------------------------------------------------ */
/* Measurement CRUD                                                    */
/* ------------------------------------------------------------------ */

export function subscribePageMeasurements(
  documentId: string,
  pageNumber: number,
  callback: (list: PdfMeasurement[]) => void,
): () => void {
  const q = query(
    collection(db, MEAS_COLLECTION),
    where('documentId', '==', documentId),
    where('pageNumber', '==', pageNumber),
  );
  return onSnapshot(q, snap => {
    const list = snap.docs.map(d => measurementFromDoc({ id: d.id, data: () => d.data() }));
    list.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    callback(list);
  });
}

/**
 * Document-wide measurement subscription. Hand the full list to the consumer
 * and let them filter by page in memory. Cheaper than re-subscribing per page
 * navigation when a viewer flips through many pages.
 */
export function subscribeDocumentMeasurements(
  documentId: string,
  callback: (list: PdfMeasurement[]) => void,
): () => void {
  const q = query(collection(db, MEAS_COLLECTION), where('documentId', '==', documentId));
  return onSnapshot(q, snap => {
    const list = snap.docs.map(d => measurementFromDoc({ id: d.id, data: () => d.data() }));
    list.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    callback(list);
  });
}

/** One-shot fetch of all measurements for a document. */
export async function fetchDocumentMeasurements(documentId: string): Promise<PdfMeasurement[]> {
  const q = query(collection(db, MEAS_COLLECTION), where('documentId', '==', documentId));
  const snap = await getDocs(q);
  const list = snap.docs.map(d => measurementFromDoc({ id: d.id, data: () => d.data() }));
  list.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  return list;
}

/** One-shot fetch of all page calibrations for a document. */
export async function fetchDocumentCalibrations(documentId: string): Promise<PdfMeasurementCalibration[]> {
  const q = query(collection(db, CAL_COLLECTION), where('documentId', '==', documentId));
  const snap = await getDocs(q);
  return snap.docs.map(d => calibrationFromDoc({ id: d.id, data: () => d.data() }));
}

export async function addMeasurement(input: Omit<PdfMeasurement, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  const payload = removeUndefinedDeep({
    ...input,
    createdAt: Timestamp.now(),
  });
  const ref = await addDoc(collection(db, MEAS_COLLECTION), payload);
  return ref.id;
}

export async function updateMeasurement(id: string, patch: Partial<PdfMeasurement>): Promise<void> {
  const payload = removeUndefinedDeep({
    ...patch,
    updatedAt: Timestamp.now(),
  });
  await updateDoc(doc(db, MEAS_COLLECTION, id), payload);
}

export async function deleteMeasurement(id: string): Promise<void> {
  await deleteDoc(doc(db, MEAS_COLLECTION, id));
}

/** Bulk delete every measurement on a page. Used by the "Clear measurements" button. */
export async function clearPageMeasurements(documentId: string, pageNumber: number): Promise<number> {
  const snap = await getDocs(
    query(
      collection(db, MEAS_COLLECTION),
      where('documentId', '==', documentId),
      where('pageNumber', '==', pageNumber),
    ),
  );
  await Promise.all(snap.docs.map(d => deleteDoc(doc(db, MEAS_COLLECTION, d.id))));
  return snap.size;
}
